import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, Clock, MapPin, Globe } from 'lucide-react';
import '../css/EventDetail.css';
import usePageTitle from '../hooks/usePageTitle';
import { getSingleEvent } from '../helpers/events';
import { API_BASE_URL } from '../helpers/api';
import 'leaflet/dist/leaflet.css';
import { renderHTML } from '../hooks/tools';

const EventDetail = () => {
  const { id } = useParams();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [event, setEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const eventData = await getSingleEvent(id);
      setEvent(eventData);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  usePageTitle(event ? event.name : 'Événement non trouvé');

  if (isLoading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2 text-muted">Chargement...</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          Événement non trouvé
        </div>
      </div>
    );
  }

  // Format date and time
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="event-detail">
      <div className="image-gallery">
        <div className="main-image">
          <img
            src={API_BASE_URL + event.images[activeImageIndex].url}
            alt={event.name}
          />
          <div className="image-overlay">
            <h1>{event.name}</h1>
            <div className="badges">
              {event.venueName && (
                <span className="event-badge">{event.venueName}</span>
              )}
            </div>
          </div>
        </div>
        {event.images.length > 1 && (
          <div className="thumbnail-strip">
            {event.images.map((image, index) => (
              <div
                key={index}
                className={`thumbnail ${index === activeImageIndex ? 'active' : ''}`}
                onClick={() => setActiveImageIndex(index)}
              >
                <img src={API_BASE_URL + image.url} alt={`View ${index + 1}`} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="container py-4">
        <div className="row">
          {/* Column 1: Description */}
          <div className="col-md-8">
            <div className="info-card">
              <p className="description">{renderHTML(event.description)}</p>
            </div>
          </div>

          {/* Column 2: Event Details */}
          <div className="col-md-4">
            <div className="info-card">
              <div className="event-info">
                <h3>Informations</h3>

                <div className="info-item">
                  <Calendar size={24} />
                  <div>
                    <h4>Date et heure</h4>
                    <p>{event.endTime && 'Début : '}{formatDateTime(event.startTime)}</p>
                    {event.endTime && (
                      <p>Fin : {formatDateTime(event.endTime)}</p>
                    )}
                  </div>
                </div>

                {event.location && (
                  <div className="info-item">
                    <MapPin size={24} />
                    <div>
                      <h4>Lieu</h4>
                      {event.venueName && <p>{event.venueName}</p>}
                      <p>{event.location}</p>
                    </div>
                  </div>
                )}

                {event.url && (
                  <div className="info-item">
                    <Globe size={24} />
                    <div>
                      <h4>Plus d'informations</h4>
                      <a 
                        href={event.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="event-cta"
                      >
                        Voir l'événement
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetail;